import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Heart, Reply, Trash2, Loader2, User, Send, ChevronDown, ChevronUp 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CommentItem({ comment, onReply, onDelete, onLike, currentUserId, contentId }) {
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);
  const token = localStorage.getItem('token');

  const loadReplies = async () => {
    setLoadingReplies(true);
    try {
      const res = await fetch(
        `${API_URL}/api/social/comments/${contentId}/replies/${comment.id}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      if (res.ok) {
        setReplies(await res.json());
      }
    } catch (error) {
      console.error('Failed to load replies:', error);
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleShowReplies = () => {
    if (!showReplies && comment.replies_count > 0) {
      loadReplies();
    }
    setShowReplies(!showReplies);
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || !token) return;
    
    setSubmittingReply(true);
    try {
      const res = await fetch(`${API_URL}/api/social/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content_id: contentId,
          content: replyText.trim(),
          parent_id: comment.id
        })
      });

      if (res.ok) {
        const newReply = await res.json();
        setReplies(prev => [...prev, newReply]);
        setReplyText('');
        setShowReplyInput(false);
        comment.replies_count += 1;
        toast.success('Reply posted');
      }
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="group" data-testid={`comment-${comment.id}`}>
      <div className="flex gap-3">
        {comment.author_avatar ? (
          <img 
            src={comment.author_avatar} 
            alt={comment.author_name}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-white" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="bg-white/5 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-white text-sm">{comment.author_name}</span>
              <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
            </div>
            <p className="text-gray-300 text-sm whitespace-pre-wrap">{comment.content}</p>
          </div>
          
          <div className="flex items-center gap-4 mt-2 ml-2">
            <button
              onClick={() => onLike(comment.id)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-pink-400 transition-colors"
              data-testid={`like-comment-${comment.id}`}
            >
              <Heart className="w-3.5 h-3.5" />
              <span>{comment.likes || 0}</span>
            </button>
            
            {token && (
              <button
                onClick={() => setShowReplyInput(!showReplyInput)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-cyan-400 transition-colors"
              >
                <Reply className="w-3.5 h-3.5" />
                <span>Reply</span>
              </button>
            )}
            
            {comment.replies_count > 0 && (
              <button
                onClick={handleShowReplies}
                className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                {showReplies ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <span>{comment.replies_count} {comment.replies_count === 1 ? 'reply' : 'replies'}</span>
              </button>
            )}
            
            {currentUserId === comment.author_id && (
              <button
                onClick={() => onDelete(comment.id)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Reply Input */}
          <AnimatePresence>
            {showReplyInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 flex gap-2"
              >
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write a reply..."
                  className="flex-1 min-h-[60px] bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-sm"
                />
                <Button
                  onClick={handleSubmitReply}
                  disabled={!replyText.trim() || submittingReply}
                  size="sm"
                  className="bg-cyan-500 hover:bg-cyan-600 text-white self-end"
                >
                  {submittingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Replies */}
          <AnimatePresence>
            {showReplies && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 ml-4 space-y-3 border-l-2 border-white/10 pl-4"
              >
                {loadingReplies ? (
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading replies...
                  </div>
                ) : (
                  replies.map(reply => (
                    <div key={reply.id} className="flex gap-2">
                      {reply.author_avatar ? (
                        <img 
                          src={reply.author_avatar} 
                          alt={reply.author_name}
                          className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                          <User className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="bg-white/5 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white text-xs">{reply.author_name}</span>
                            <span className="text-xs text-gray-500">{formatDate(reply.created_at)}</span>
                          </div>
                          <p className="text-gray-300 text-xs">{reply.content}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export function CommentsSection({ contentId, initialCount = 0 }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentCount, setCommentCount] = useState(initialCount);
  const token = localStorage.getItem('token');
  
  let currentUserId = null;
  if (token) {
    try {
      currentUserId = JSON.parse(atob(token.split('.')[1])).user_id;
    } catch (e) {}
  }

  useEffect(() => {
    loadComments();
  }, [contentId]);

  const loadComments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/comments/${contentId}`);
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !token) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/social/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content_id: contentId,
          content: newComment.trim()
        })
      });

      if (res.ok) {
        const comment = await res.json();
        setComments(prev => [comment, ...prev]);
        setNewComment('');
        setCommentCount(prev => prev + 1);
        toast.success('Comment posted');
      } else {
        toast.error('Failed to post comment');
      }
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (commentId) => {
    if (!token) {
      toast.error('Please log in to like comments');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/social/comments/${commentId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const { liked, likes } = await res.json();
        setComments(prev => 
          prev.map(c => c.id === commentId ? { ...c, likes } : c)
        );
      }
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  const handleDelete = async (commentId) => {
    if (!confirm('Delete this comment?')) return;

    try {
      const res = await fetch(`${API_URL}/api/social/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setComments(prev => prev.filter(c => c.id !== commentId));
        setCommentCount(prev => prev - 1);
        toast.success('Comment deleted');
      }
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  return (
    <div className="bg-[#0d1525] rounded-xl border border-white/10 p-6" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="w-5 h-5 text-cyan-400" />
        <h3 className="font-semibold text-white">Comments</h3>
        <span className="text-sm text-gray-400">({commentCount})</span>
      </div>

      {/* New Comment Form */}
      {token ? (
        <form onSubmit={handleSubmit} className="mb-6">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            className="mb-3 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            rows={3}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!newComment.trim() || submitting}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Post Comment
                </>
              )}
            </Button>
          </div>
        </form>
      ) : (
        <div className="mb-6 p-4 bg-white/5 rounded-lg text-center">
          <p className="text-gray-400 text-sm">
            <a href="/login" className="text-cyan-400 hover:underline">Enter</a> to join the conversation
          </p>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>No comments yet</p>
          <p className="text-sm">Be the first to share your thoughts!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onReply={() => {}}
              onDelete={handleDelete}
              onLike={handleLike}
              currentUserId={currentUserId}
              contentId={contentId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CommentsSection;
